import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart2,
  CheckCircle,
  Cpu,
  HardDrive,
  Monitor,
  RefreshCw,
  Server,
  Clock,
  Wifi,
  Users,
  TrendingUp,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ComposedChart,
  Line,
  ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { authAPI, adminAPI } from '../services/api';
import type { SystemOverview, SystemOverviewHistory, CapacityHistory, SystemHealth } from '../types';
import { getVersionString } from '../version';

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString() : '-';

const formatBytes = (bytes?: number | null) => {
  if (!bytes) return '-';
  if (bytes >= 1024 ** 3) {
    return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  }
  if (bytes >= 1024 ** 2) {
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const formatHour = (hourStr: string) => {
  const d = new Date(hourStr);
  return `${d.getHours()}:00`;
};

export default function AdminSystemMonitor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<SystemOverview | null>(null);
  const [historyData, setHistoryData] = useState<SystemOverviewHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [chartView, setChartView] = useState<'daily' | 'hourly'>('daily');
  const [capacityPeriod, setCapacityPeriod] = useState<'24h' | '7d' | '30d'>('24h');
  const [capacityData, setCapacityData] = useState<CapacityHistory | null>(null);
  const [healthData, setHealthData] = useState<SystemHealth | null>(null);

  const loadData = useCallback(async () => {
    setError(null);
    const authCheck = await authAPI.checkAdminAuth();
    if (!authCheck.authenticated) {
      navigate('/admin');
      return;
    }
    const [overview, history, capacity, health] = await Promise.all([
      adminAPI.getSystemOverview(),
      adminAPI.getSystemOverviewHistory(),
      adminAPI.getCapacityHistory(capacityPeriod).catch(() => null),
      adminAPI.getSystemHealth().catch(() => null),
    ]);
    setData(overview);
    setHistoryData(history);
    setCapacityData(capacity);
    setHealthData(health);
    setLastUpdated(new Date().toLocaleTimeString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false }));
  }, [navigate, capacityPeriod]);

  // Refetch capacity when period changes (without re-running everything else)
  useEffect(() => {
    adminAPI.getCapacityHistory(capacityPeriod)
      .then(setCapacityData)
      .catch(() => setCapacityData(null));
  }, [capacityPeriod]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadData();
      } catch (err: any) {
        console.error('System monitor error', err);
        setError(err.message || 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadData]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadData().catch((err) => {
        console.error('Auto refresh error', err);
        setError((err as Error)?.message || '自動更新に失敗しました');
      });
    }, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);
      await loadData();
    } catch (err: any) {
      console.error('Manual refresh error', err);
      setError(err.message || 'データの取得に失敗しました');
    } finally {
      setRefreshing(false);
    }
  };

  const getServiceColor = (state: string) => {
    if (state.toLowerCase().includes('running')) return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/30';
    if (state.toLowerCase().includes('exited') || state.toLowerCase().includes('dead')) {
      return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30';
    }
    return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700/50';
  };

  const cpuLoad = data?.systemMetrics.cpuLoad;
  const memory = data?.systemMetrics.memory;
  const disk = data?.systemMetrics.disk;
  const cronStatus = data?.cronStatus;
  const cronKnown = Boolean(cronStatus);
  const cronHealthy = Boolean(cronStatus?.active && cronStatus?.enabled);
  const cronBadgeClass = cronHealthy
    ? 'text-green-700 bg-green-50'
    : cronKnown
      ? 'text-red-700 bg-red-50'
      : 'text-amber-700 bg-amber-50';
  const cronBadgeLabel = cronHealthy ? t('systemMonitor.normalOperation') : cronKnown ? t('systemMonitor.needsCheck') : t('systemMonitor.fetchingInfo');

  const vpnStatus = historyData?.vpnStatus;

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Page header (sidebar handles global nav, theme, language, logout) */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <Monitor className="w-8 h-8 text-cyan-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('systemMonitor.title')}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">キャプチャ基盤の稼働状況とトラフィック分析</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">{getVersionString()}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('systemMonitor.lastUpdate')}: {lastUpdated || '未更新'}（{t('systemMonitor.autoRefresh')}）
            </p>
            {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
          </div>
          <button
            onClick={handleManualRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors disabled:opacity-60"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? t('common.refreshing') : t('systemMonitor.getLatest')}</span>
          </button>
        </div>

        {loading ? (
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-10 text-center text-gray-600 dark:text-gray-400">
            {t('common.loading')}
          </div>
        ) : (
          <>
            {/* System Health (watchdog) — fed by the cached health_watchdog.sh output (≈0 cost) */}
            {healthData && (() => {
              const hs = (healthData.status || 'unknown').toUpperCase();
              const isOk = hs === 'OK';
              const isAlert = hs === 'ALERT';
              const badge = isOk
                ? 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/30'
                : isAlert
                  ? 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30'
                  : 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30';
              let staleMin: number | null = null;
              if (healthData.ts) {
                const diff = (Date.now() - new Date(healthData.ts).getTime()) / 60000;
                if (!Number.isNaN(diff)) staleMin = Math.max(Math.floor(diff), 0);
              }
              const stale = staleMin !== null && staleMin > 12;
              const metric = (label: string, val?: string) => (
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{val ?? '-'}</div>
                </div>
              );
              return (
                <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6 mb-8">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      {isAlert
                        ? <AlertTriangle className="w-5 h-5 text-red-500" />
                        : <CheckCircle className="w-5 h-5 text-green-500" />}
                      <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">🩺 {t('systemMonitor.health.title')}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      {healthData.slackEnabled && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/30">{t('systemMonitor.health.slackOn')}</span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge}`}>
                        {isOk ? t('systemMonitor.health.ok') : isAlert ? t('systemMonitor.health.alert') : hs}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {metric(t('systemMonitor.health.rawEventsAge'), healthData.raw_events_age_s ? `${healthData.raw_events_age_s}s` : '-')}
                    {metric(t('systemMonitor.health.wgSnapshotAge'), healthData.wg_snapshot_age_s ? `${healthData.wg_snapshot_age_s}s` : '-')}
                    {metric(t('systemMonitor.health.diskPercent'), healthData.disk_pct ? `${healthData.disk_pct}%` : '-')}
                    {metric(t('systemMonitor.health.backupAgeHours'), healthData.backup_age_h ? `${healthData.backup_age_h}h` : '-')}
                    {metric(t('systemMonitor.health.lastChecked'), staleMin !== null ? `${staleMin}m` : '-')}
                  </div>
                  {stale && (
                    <div className="mt-3 text-sm text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30 rounded-lg px-3 py-2">
                      ⚠ {t('systemMonitor.health.stale')}
                    </div>
                  )}
                  {healthData.recentAlerts && healthData.recentAlerts.length > 0 && (() => {
                    const lastLine = healthData.recentAlerts[healthData.recentAlerts.length - 1];
                    const m = lastLine.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)/);
                    let rel = '';
                    if (m) {
                      const mins = Math.max(Math.floor((Date.now() - new Date(m[1]).getTime()) / 60000), 0);
                      rel = mins < 60 ? `${mins}m` : mins < 1440 ? `${Math.floor(mins / 60)}h` : `${Math.floor(mins / 1440)}d`;
                    }
                    // When current status is OK, don't show the (resolved) alert lines — just a muted one-liner.
                    if (isOk) {
                      return (
                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                          {t('systemMonitor.health.lastAlertResolved', { time: rel || '?' })}
                        </div>
                      );
                    }
                    return (
                      <div className="mt-3">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('systemMonitor.health.recentAlerts')}</div>
                        <div className="space-y-1">
                          {healthData.recentAlerts.map((line, i) => (
                            <div key={i} className="text-xs font-mono text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1 truncate">{line}</div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

            {/* Top cards - 5 columns including VPN status */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-5">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                  <BarChart2 className="w-5 h-5 text-blue-500" />
                  <span className="text-sm">総広告数</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {formatNumber(data?.adStats.totalAds)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  24時間: {formatNumber(data?.adStats.adsLast24h)}
                </p>
              </div>

              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-5">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                  <Wifi className="w-5 h-5 text-green-500" />
                  <span className="text-sm">VPN接続</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {vpnStatus?.activePeers ?? 0}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">/ {vpnStatus?.totalPeers ?? 0}</span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">アクティブ / 全ピア</p>
              </div>

              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-5">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  <span className="text-sm">ユニークユーザー (7日)</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">
                  {historyData?.userActivity.length ?? 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  計 {historyData?.userActivity.reduce((sum, u) => sum + u.events, 0) ?? 0} イベント
                </p>
              </div>

              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-5">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                  <Activity className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm">メモリ</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {memory ? `${memory.usedPercent.toFixed(0)}%` : '-'}
                </p>
                <div className="w-full bg-gray-200 dark:bg-warm-gray-700 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-emerald-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${memory ? memory.usedPercent : 0}%` }}
                  />
                </div>
              </div>

              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-5">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                  <HardDrive className="w-5 h-5 text-amber-500" />
                  <span className="text-sm">ディスク</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {disk ? `${disk.usedPercent.toFixed(0)}%` : '-'}
                </p>
                <div className="w-full bg-gray-200 dark:bg-warm-gray-700 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-amber-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${disk ? disk.usedPercent : 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Traffic Trend Graph */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  <h2 className="text-lg font-bold">トラフィック推移</h2>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setChartView('daily')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      chartView === 'daily'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                    }`}
                  >
                    日別 (7日)
                  </button>
                  <button
                    onClick={() => setChartView('hourly')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      chartView === 'hourly'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                    }`}
                  >
                    時間別 (24h)
                  </button>
                </div>
              </div>

              {chartView === 'daily' && historyData?.dailyEvents && historyData.dailyEvents.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={historyData.dailyEvents}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      labelFormatter={(label) => `日付: ${formatDate(label)}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="youtube" name="YouTube" fill="#ef4444" stackId="a" />
                    <Bar dataKey="facebook" name="Facebook" fill="#3b82f6" stackId="a" />
                    <Bar dataKey="instagram" name="Instagram" fill="#ec4899" stackId="a" />
                    <Bar dataKey="xTwitter" name="X" fill="#374151" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : chartView === 'hourly' && historyData?.hourlyEvents && historyData.hourlyEvents.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={historyData.hourlyEvents}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="hour"
                      tickFormatter={formatHour}
                      tick={{ fontSize: 12 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      labelFormatter={(label) => `時刻: ${formatHour(label)}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      name="イベント数"
                      stroke="#3b82f6"
                      fill="#93c5fd"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400 dark:text-gray-500">
                  {t('common.noData')}
                </div>
              )}
            </div>

            {/* === Capacity Monitoring (Migration 013, Apr 23) === */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                  <Cpu className="w-5 h-5 text-fuchsia-500" />
                  <h2 className="text-lg font-bold">📊 {t('capacity.title')}</h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    {t('capacity.subtitle')}
                  </span>
                </div>
                <div className="flex gap-2">
                  {(['24h','7d','30d'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setCapacityPeriod(p)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        capacityPeriod === p
                          ? 'bg-fuchsia-600 text-white'
                          : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                      }`}
                    >
                      {t(`capacity.period${p === '24h' ? '24h' : p === '7d' ? '7d' : '30d'}`)}
                    </button>
                  ))}
                </div>
              </div>

              {capacityData && capacityData.points.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={360}>
                    <ComposedChart data={capacityData.points.map(p => ({
                      ts: p.ts,
                      cpu: p.cpuPct,
                      mem: p.memUsedPct,
                      disk: p.diskUsedPct,
                      users: p.activeUsers5m,
                      netRx: p.netRxKbps,
                      netTx: p.netTxKbps,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="ts"
                        tick={{ fontSize: 11 }}
                        stroke="#9ca3af"
                        tickFormatter={(ts: string) => {
                          const d = new Date(ts);
                          return capacityPeriod === '24h'
                            ? `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
                            : `${d.getMonth()+1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis yAxisId="pct" orientation="left"
                        domain={[0, (dataMax: number) => Math.max(110, Math.ceil((dataMax + 20) / 50) * 50)]}
                        tick={{ fontSize: 11 }} stroke="#9ca3af"
                        label={{ value: '%', angle: 0, position: 'insideTopLeft', fontSize: 11, fill: '#6b7280' }}
                      />
                      <YAxis yAxisId="users" orientation="right" tick={{ fontSize: 11 }} stroke="#9ca3af"
                        label={{ value: 'users / kbps', angle: 0, position: 'insideTopRight', fontSize: 11, fill: '#6b7280' }}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                        labelFormatter={(label: string) => new Date(label).toLocaleString('ja-JP')}
                        formatter={(val: any, name: string) => {
                          if (val == null) return ['-', name];
                          if (name === 'CPU%' || name === 'Mem%' || name === 'Disk%') return [`${Number(val).toFixed(1)}%`, name];
                          if (name === 'NetRx kbps' || name === 'NetTx kbps') return [`${Number(val).toFixed(0)} kbps`, name];
                          return [String(val), name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />

                      {/* Capacity threshold lines */}
                      <ReferenceLine yAxisId="pct" y={70} stroke="#f59e0b" strokeDasharray="6 4"
                        label={{ value: t('capacity.warning70'), fontSize: 10, fill: '#f59e0b', position: 'right' }} />
                      <ReferenceLine yAxisId="pct" y={90} stroke="#ef4444" strokeDasharray="6 4"
                        label={{ value: t('capacity.critical90'), fontSize: 10, fill: '#ef4444', position: 'right' }} />
                      <ReferenceLine yAxisId="pct" y={100} stroke="#7c3aed" strokeWidth={2}
                        label={{ value: t('capacity.fullCapacity100'), fontSize: 10, fill: '#7c3aed', position: 'right' }} />

                      <Line yAxisId="pct"   type="monotone" dataKey="cpu"   name={t('capacity.cpu')    as string} stroke="#3b82f6" strokeWidth={2} dot={false} isAnimationActive={false} />
                      <Line yAxisId="pct"   type="monotone" dataKey="mem"   name={t('capacity.mem')    as string} stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} />
                      <Line yAxisId="pct"   type="monotone" dataKey="disk"  name={t('capacity.disk')   as string} stroke="#a855f7" strokeWidth={2} dot={false} isAnimationActive={false} />
                      <Line yAxisId="users" type="monotone" dataKey="users" name={t('capacity.users5m') as string} stroke="#f97316" strokeWidth={2} dot={false} isAnimationActive={false} />
                      <Line yAxisId="users" type="monotone" dataKey="netRx" name={t('capacity.netRx')  as string} stroke="#06b6d4" strokeWidth={1} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
                      <Line yAxisId="users" type="monotone" dataKey="netTx" name={t('capacity.netTx')  as string} stroke="#0ea5e9" strokeWidth={1} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>{t('capacity.interval')}</div>
                    <div>{t('capacity.retention')}</div>
                    <div>{t('capacity.samples', { count: capacityData.points.length })}</div>
                    <div>{capacityPeriod === '24h' ? t('capacity.periodLabel24h') : capacityPeriod === '7d' ? t('capacity.periodLabel7d') : t('capacity.periodLabel30d')}</div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 mt-3 px-3 py-2 bg-violet-50 dark:bg-violet-900/20 border-l-4 border-violet-400 rounded">
                    💡 {t('capacity.cpuExplain')}
                  </div>
                </>
              ) : (
                <div className="h-72 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm text-center px-4">
                  {t('capacity.collecting')}
                </div>
              )}
            </div>

            {/* User Activity & VPN Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* User Activity Table */}
              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 mb-4">
                  <Users className="w-5 h-5 text-purple-500" />
                  <h2 className="text-lg font-bold">ユーザーアクティビティ (7日)</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 dark:text-gray-400 uppercase border-b dark:border-warm-gray-700">
                        <th className="py-2 text-left">ユーザーID</th>
                        <th className="py-2 text-right">イベント</th>
                        <th className="py-2 text-left">プラットフォーム</th>
                        <th className="py-2 text-left">最終活動</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData?.userActivity && historyData.userActivity.length > 0 ? (
                        historyData.userActivity.map((user, idx) => (
                          <tr key={idx} className="border-b dark:border-warm-gray-700 last:border-none">
                            <td className="py-2 font-mono text-gray-700 dark:text-gray-300">{user.userId}</td>
                            <td className="py-2 text-right font-semibold text-blue-600">{user.events}</td>
                            <td className="py-2">
                              <div className="flex gap-1 flex-wrap">
                                {user.platforms.map((p) => (
                                  <span
                                    key={p}
                                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                      p === 'youtube' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                                      p === 'facebook' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                                      p === 'instagram' ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300' :
                                      p === 'x_twitter' ? 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300' :
                                      'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400'
                                    }`}
                                  >
                                    {p === 'x_twitter' ? 'X' : p}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="py-2 text-gray-500 dark:text-gray-400 text-xs">
                              {new Date(user.lastActivity).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="py-4 text-center text-gray-400 dark:text-gray-500">
                            ユーザーアクティビティなし
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* VPN Peers Status */}
              <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 mb-4">
                  <Wifi className="w-5 h-5 text-green-500" />
                  <h2 className="text-lg font-bold">VPN接続状況</h2>
                  <span className={`ml-auto px-2 py-1 rounded text-xs font-semibold ${
                    (vpnStatus?.activePeers ?? 0) > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400'
                  }`}>
                    {vpnStatus?.activePeers ?? 0} 接続中
                  </span>
                </div>
                <div className="space-y-2">
                  {vpnStatus?.recentHandshakes && vpnStatus.recentHandshakes.length > 0 ? (
                    vpnStatus.recentHandshakes.map((hs, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                          hs.active ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-50 dark:bg-warm-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${hs.active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'}`} />
                          <span className="font-mono text-sm text-gray-700 dark:text-gray-200">{hs.peer}</span>
                        </div>
                        <span className={`text-xs ${hs.active ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {hs.ago}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-400 dark:text-gray-500">
                      接続中のピアなし
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                  ※ 3分以内のハンドシェイクを「アクティブ」と判定
                </p>
              </div>
            </div>

            {/* System metrics row */}
            {data?.systemMetrics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400 mb-2">
                    <Cpu className="w-5 h-5 text-indigo-500" />
                    <span>CPU {t('capacity.cpu')}</span>
                    {cpuLoad?.cpuCount ? (
                      <span className="text-xs text-gray-400 ml-auto">{t('capacity.loadVsCores', { cores: cpuLoad.cpuCount })}</span>
                    ) : null}
                  </div>
                  <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex justify-between items-center">
                      <span>1分</span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono">{cpuLoad?.load1m.toFixed(2)}</span>
                        {cpuLoad?.cpuCount ? (
                          <span className="text-xs text-violet-600 dark:text-violet-300 font-mono">
                            {((cpuLoad.load1m / cpuLoad.cpuCount) * 100).toFixed(0)}%
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>5分</span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono">{cpuLoad?.load5m.toFixed(2)}</span>
                        {cpuLoad?.cpuCount ? (
                          <span className="text-xs text-violet-600 dark:text-violet-300 font-mono">
                            {((cpuLoad.load5m / cpuLoad.cpuCount) * 100).toFixed(0)}%
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>15分</span>
                      <span className="flex items-center gap-2">
                        <span className="font-mono">{cpuLoad?.load15m.toFixed(2)}</span>
                        {cpuLoad?.cpuCount ? (
                          <span className="text-xs text-violet-600 dark:text-violet-300 font-mono">
                            {((cpuLoad.load15m / cpuLoad.cpuCount) * 100).toFixed(0)}%
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400 mb-2">
                    <Activity className="w-5 h-5 text-emerald-500" />
                    <span>メモリ使用率</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {memory ? `${memory.usedPercent.toFixed(1)}%` : '-'}
                  </p>
                  <div className="w-full bg-gray-200 dark:bg-warm-gray-700 rounded-full h-2 mt-3">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${memory ? memory.usedPercent : 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {formatBytes(memory?.usedBytes)} / {formatBytes(memory?.totalBytes)}
                  </p>
                </div>

                <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400 mb-2">
                    <HardDrive className="w-5 h-5 text-amber-500" />
                    <span>ディスク使用率</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {disk ? `${disk.usedPercent.toFixed(1)}%` : '-'}
                  </p>
                  <div className="w-full bg-gray-200 dark:bg-warm-gray-700 rounded-full h-2 mt-3">
                    <div
                      className="bg-amber-500 h-2 rounded-full transition-all"
                      style={{ width: `${disk ? disk.usedPercent : 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {formatBytes(disk?.usedBytes)} / {formatBytes(disk?.totalBytes)}
                  </p>
                </div>
              </div>
            )}

            {/* Cron status */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6 mb-8">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 mb-3">
                <Clock className="w-5 h-5 text-sky-500" />
                <h2 className="text-lg font-bold">スケジュールジョブ (cron)</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${cronBadgeClass}`}>
                  {cronBadgeLabel}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-50 dark:bg-warm-gray-700 rounded-lg px-4 py-3 flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">サービス状態</span>
                  <span
                    className={`font-semibold ${
                      cronStatus?.active === undefined
                        ? 'text-gray-500'
                        : cronStatus.active
                          ? 'text-green-700'
                          : 'text-red-600'
                    }`}
                  >
                    {cronStatus?.active === undefined
                      ? '取得不可'
                      : cronStatus.active
                        ? 'Active (running)'
                        : 'Inactive'}
                  </span>
                </div>
                <div className="bg-slate-50 dark:bg-warm-gray-700 rounded-lg px-4 py-3 flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">再起動時の自動起動</span>
                  <span
                    className={`font-semibold ${
                      cronStatus?.enabled === undefined
                        ? 'text-gray-500'
                        : cronStatus.enabled
                          ? 'text-green-700'
                          : 'text-red-600'
                    }`}
                  >
                    {cronStatus?.enabled === undefined
                      ? '取得不可'
                      : cronStatus.enabled
                        ? 'Enabled'
                        : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Service status table */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6 mb-8">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 mb-4">
                <Server className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-bold">Docker サービス稼働状況</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-sm text-gray-500 dark:text-gray-400 uppercase border-b dark:border-warm-gray-700">
                      <th className="py-3">サービス名</th>
                      <th className="py-3">状態</th>
                      <th className="py-3">ステータス</th>
                      <th className="py-3">稼働時間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.services.length ? (
                      data.services.map((service) => (
                        <tr key={service.name} className="border-b dark:border-warm-gray-700 last:border-none text-sm">
                          <td className="py-3 font-semibold text-gray-800 dark:text-gray-200">{service.name}</td>
                          <td className="py-3">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${getServiceColor(service.state || '')}`}
                            >
                              {service.state || 'unknown'}
                            </span>
                          </td>
                          <td className="py-3 text-gray-700 dark:text-gray-300">{service.status || '-'}</td>
                          <td className="py-3 text-gray-500 dark:text-gray-400">{service.runningFor || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-6 text-center text-gray-500 dark:text-gray-400">
                          サービス情報を取得できませんでした
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Platforms summary */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 mb-4">
                <BarChart2 className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-bold">プラットフォーム別集計</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'YouTube', value: data?.adStats.youtubeAds, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' },
                  { label: 'Facebook', value: data?.adStats.facebookAds, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                  { label: 'Instagram', value: data?.adStats.instagramAds, color: 'text-pink-600', bg: 'bg-pink-50 dark:bg-pink-900/20' },
                  { label: 'X', value: data?.adStats.xTwitterAds, color: 'text-gray-900 dark:text-gray-100', bg: 'bg-gray-50 dark:bg-warm-gray-700' },
                ].map(({ label, value, color, bg }) => (
                  <div key={label} className={`${bg} rounded-lg p-4 text-center`}>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{label}</div>
                    <div className={`text-2xl font-bold ${color}`}>{formatNumber(value ?? 0)}</div>
                  </div>
                ))}
              </div>
            </div>

            {(() => {
              const errCount = data?.metadataStats.error ?? 0;
              const total = data?.metadataStats.total ?? 0;
              // 404s, timeouts, and dead landing pages produce a steady baseline
              // of enrichment errors. Only flag if the error rate is meaningfully
              // high (>5% of the enrichment pipeline output).
              const errPct = total > 0 ? (errCount / total) * 100 : 0;
              const elevated = errPct > 5;
              if (elevated) {
                return (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mt-8 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5" />
                    <div>
                      <p className="font-semibold">{t('systemMonitor.errorRateHigh')}</p>
                      <p className="text-sm">
                        {t('systemMonitor.errorRateDetail', {
                          errCount: errCount.toLocaleString(),
                          total: total.toLocaleString(),
                          errPct: errPct.toFixed(1),
                        })}
                      </p>
                    </div>
                  </div>
                );
              }
              return (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-4 mt-8 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-semibold">{t('systemMonitor.operational')}</p>
                    <p className="text-sm">
                      {t('systemMonitor.operationalDetail')}
                      {total > 0 && (
                        <> {t('systemMonitor.enrichmentSummary', {
                          success: (total - errCount).toLocaleString(),
                          total: total.toLocaleString(),
                        })}</>
                      )}
                    </p>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </main>
    </div>
  );
}
